/**
 * PinPad — 3x4 number pad (0-9 + backspace).
 *
 * Per doc 08 §10.1:
 *  - 60x60pt minimum per button (we use 72x72 for easier thumb reach)
 *  - Voice-over labels: "Angka 1" / "Hapus digit terakhir"
 *  - Haptic on press (Light) when haptics are available
 *  - Reduced-motion safe (no animations)
 *
 * Layout (3 cols × 4 rows):
 *   1  2  3
 *   4  5  6
 *   7  8  9
 *   .  0  ⌫
 */

import { Pressable, StyleSheet, Text, View } from 'react-native';
import * as Haptics from 'expo-haptics';

import { Brand, Radii } from '@/constants/theme';

interface PinPadProps {
  /** Called when a digit (0-9) is pressed. */
  onDigit: (digit: string) => void;
  /** Called when backspace is pressed. */
  onBackspace: () => void;
  /** Disable all input (during loading / cooldown). */
  disabled?: boolean;
}

export function PinPad({ onDigit, onBackspace, disabled = false }: PinPadProps) {
  const handleDigit = (d: string) => {
    if (disabled) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    onDigit(d);
  };
  const handleBackspace = () => {
    if (disabled) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    onBackspace();
  };

  return (
    <View style={styles.grid}>
      {(['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const).map((d) => (
        <PinKey
          key={d}
          label={d}
          onPress={() => handleDigit(d)}
          disabled={disabled}
        />
      ))}
      <View style={styles.emptyKey} />
      <PinKey label="0" onPress={() => handleDigit('0')} disabled={disabled} />
      <PinKey
        label="⌫"
        onPress={handleBackspace}
        variant="backspace"
        disabled={disabled}
        accessibilityLabel="Hapus digit terakhir"
      />
    </View>
  );
}

interface PinKeyProps {
  label: string;
  onPress: () => void;
  variant?: 'digit' | 'backspace';
  disabled?: boolean;
  /** Override the default a11y label. */
  accessibilityLabel?: string;
}

function PinKey({
  label,
  onPress,
  variant = 'digit',
  disabled = false,
  accessibilityLabel,
}: PinKeyProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.key,
        variant === 'backspace' && styles.keyBackspace,
        pressed && !disabled && styles.keyPressed,
        disabled && styles.keyDisabled,
      ]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? `Angka ${label}`}
      accessibilityState={{ disabled }}
    >
      <Text
        style={[
          styles.keyText,
          variant === 'backspace' && styles.keyTextBackspace,
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

const KEY_SIZE = 72;

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: KEY_SIZE * 3 + 12 * 2,
    justifyContent: 'space-between',
    rowGap: 12,
  },
  key: {
    width: KEY_SIZE,
    height: KEY_SIZE,
    borderRadius: Radii.medium,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyBackspace: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  keyPressed: {
    backgroundColor: Brand.primary,
    borderColor: Brand.primary,
  },
  keyDisabled: {
    opacity: 0.4,
  },
  keyText: {
    fontSize: 28,
    fontWeight: '500',
    color: '#1A0B5C',
  },
  keyTextBackspace: {
    fontSize: 28,
    color: Brand.primary,
    fontWeight: '600',
  },
  emptyKey: {
    width: KEY_SIZE,
    height: KEY_SIZE,
  },
});
